/**
 * Fotos vor dem Hochladen verkleinern — ohne die Angaben zu verlieren.
 *
 * Der Server kann das nicht: ohne ffmpeg und ohne verlässliche GD-Ausstattung
 * bleibt ihm nur, entgegenzunehmen, was kommt. Also rechnet der Browser --
 * und das ist ohnehin die bessere Stelle, weil dann auch die Leitung
 * unterwegs nicht das volle Bild tragen muss.
 *
 * Ein iPhone-Foto von 3 MB wird so zu 300 bis 600 kB. Bei 40 GB freiem
 * Speicher ist das der Unterschied zwischen 13.000 und 90.000 Bildern.
 *
 * **Die EXIF-Daten bleiben erhalten**, allen voran der Aufnahmeort: sie
 * werden aus dem Original übernommen und in das verkleinerte Bild gesetzt
 * (siehe `exif.ts`). Bei einem Reisebericht ist der Ort die Angabe, nach der
 * man Jahre später sucht.
 *
 * Ein Foto, das ohnehin klein genug ist, wird **gar nicht angefasst** und
 * unverändert hochgeladen. Das erhält nicht nur alle Angaben, sondern auch
 * die volle Bildqualität — neu zeichnen kostet immer etwas.
 */

import { baueExif, findExif, isJpeg, readAufnahme, uprightExif, withExif } from './exif'
import type { Aufnahme } from './exif'

/**
 * Lange Kante und Qualität -- die Vorgaben, wenn der Server nichts sagt.
 *
 * Sie stehen hier als Rückfalllinie, nicht als Gesetz: die geltenden Werte
 * kommen aus den Einstellungen (`/api/settings`) und lassen sich im Betrieb
 * ändern, ohne die Oberfläche neu zu bauen. Nur solange diese Abfrage noch
 * unterwegs ist -- oder scheitert -- gelten diese zwei Zahlen.
 */
export const MAX_EDGE = 2560
export const QUALITY = 80

/** Bis hierher lohnt das Verkleinern nicht. */
const KEEP_ORIGINAL_BYTES = 4 * 1024 * 1024

export interface Prepared {
  blob: Blob
  width: number
  height: number
  /**
   * Wo und wann das Foto entstanden ist -- aus dem **Original** gelesen.
   *
   * Der Server bekommt es als Kopfzeile mitgeschickt und legt es an der Datei
   * ab. Er selbst kann es nicht lesen: EXIF auseinanderzunehmen ist Arbeit,
   * die auf einem geteilten Paket nichts zu suchen hat, und der Browser hat
   * die Datei ohnehin schon offen.
   */
  aufnahme: Aufnahme
}

/** Die eingestellte Bildgröße; `quality` in Prozent, wie sie der Server führt. */
export interface ImageLimits {
  // Freiwillig: solange die Einstellungen noch unterwegs sind, gelten die
  // Vorgaben oben. `prepareImage()` setzt sie ohnehin selbst ein.
  maxEdge?: number
  quality?: number
  /**
   * Die Uhr der Reise — für Bilder, deren EXIF-Block keine Zone trägt.
   *
   * `DateTimeOriginal` ist Ortszeit ohne Zone. Ohne diese Angabe läse der
   * Browser sie in **seiner** Uhr, und in der Nachbearbeitung zu Hause landet
   * ein Foto damit Stunden neben seinem Platz. Die Reise weiß dagegen, wo die
   * Kamera stand.
   */
  reiseVersatz?: number | null
}

export async function prepareImage(file: File, limits?: ImageLimits): Promise<Prepared> {
  const maxEdge = limits?.maxEdge ?? MAX_EDGE
  const quality = (limits?.quality ?? QUALITY) / 100

  const original = new Uint8Array(await file.arrayBuffer())
  // Vor allem anderen: das Verkleinern rechnet die Bildpunkte neu, und der
  // Block wird nur nachträglich wieder eingesetzt. Aus dem Original gelesen,
  // ist die Angabe von dem, was der Fotoapparat geschrieben hat -- nicht von
  // dem, was danach noch übrig war.
  const aufnahme = readAufnahme(original, limits?.reiseVersatz ?? null)
  const bitmap = await toBitmap(file)
  const longest = Math.max(bitmap.width, bitmap.height)

  // Klein genug und schon JPEG: unverändert weiterreichen.
  if (isJpeg(original) && longest <= maxEdge && original.byteLength <= KEEP_ORIGINAL_BYTES) {
    const masse = { width: bitmap.width, height: bitmap.height }
    bitmap.close?.()
    return { blob: new Blob([original], { type: 'image/jpeg' }), ...masse, aufnahme }
  }

  const scale = Math.min(1, maxEdge / longest)
  const width = Math.max(1, Math.round(bitmap.width * scale))
  const height = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Dieser Browser kann das Bild nicht verkleinern.')
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()

  const gezeichnet = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  )
  if (!gezeichnet) throw new Error('Das Bild ließ sich nicht umwandeln.')

  return { blob: await keepExif(gezeichnet, original, aufnahme), width, height, aufnahme }
}

/**
 * Aufnahmeort und -zeit aus einem fertigen Blob.
 *
 * Für den Ausgangskorb: dort liegt das verkleinerte Bild, und der EXIF-Block
 * steckt noch darin (siehe `keepExif`). Ohne diesen Weg verlöre ein offline
 * geschriebener Beitrag genau die Angaben, um die es geht — und offline
 * geschrieben wird auf einer Reise oft.
 */
export async function aufnahmeAus(
  blob: Blob,
  reiseVersatz: number | null = null,
): Promise<Aufnahme> {
  try {
    return readAufnahme(new Uint8Array(await blob.arrayBuffer()), reiseVersatz)
  } catch {
    return { at: null, lat: null, lon: null, tz_offset_min: null, befund: 'unlesbar' }
  }
}

/**
 * Die Angaben als Kopfzeilen für den Upload.
 *
 * Als Kopfzeilen und nicht im Rumpf, weil der Rumpf die Bilddatei ist. Was
 * fehlt, wird weggelassen — eine leere Kopfzeile wäre eine Behauptung über
 * nichts, und der Server müsste sie wieder auseinandernehmen.
 */
export function aufnahmeKopf(aufnahme: Aufnahme | undefined): Record<string, string> {
  const kopf: Record<string, string> = {}
  if (!aufnahme) return kopf
  if (aufnahme.at !== null) kopf['x-aufnahme-zeit'] = aufnahme.at
  if (aufnahme.lat !== null && aufnahme.lon !== null) {
    kopf['x-aufnahme-ort'] = `${aufnahme.lat},${aufnahme.lon}`
  }
  return kopf
}

/**
 * Ob die Datei sich sofort als Vorschau zeigen lässt.
 *
 * Für die Vorschau vor dem Verkleinern: eine HEIC-Datei vom iPhone zeigt nur
 * Safari an, in Chrome und Firefox bliebe die Kachel schwarz. Dort wird auf
 * das umgewandelte Bild gewartet -- ein paar Zehntelsekunden später, dafür
 * ohne Loch.
 */
export function canPreviewDirectly(file: File): boolean {
  return /^image\/(jpeg|png|webp|gif)$/.test(file.type)
}

/**
 * Nimmt den EXIF-Block des Originals in das neue Bild mit.
 *
 * Geht nur, wenn das Original ein JPEG war. Eine HEIC-Datei vom iPhone trägt
 * ihre Angaben in einem anderen Behälter, an den der Browser nicht
 * heranlässt; dort bleibt es beim reinen Bild. Auf dem iPhone gibt die
 * Dateiauswahl allerdings meist ohnehin ein umgewandeltes JPEG heraus — und
 * das trägt die Angaben mit.
 */
async function keepExif(
  gezeichnet: Blob,
  original: Uint8Array,
  aufnahme: Aufnahme,
): Promise<Blob> {
  // **Aus einem JPEG wird übernommen, aus allem anderen geschrieben.**
  //
  // Übernehmen ist das Bessere, wo es geht: der Block trägt dann auch Kamera,
  // Belichtung und alles Übrige. Aus einem DNG geht es nicht -- dessen
  // Verzeichnisse verweisen auf Bilddaten von vielen Megabyte, und ein
  // APP1-Segment fasst 64 kB. Dann entsteht ein kleiner neuer aus dem, was
  // gelesen wurde.
  const vorhanden = findExif(original)
  const block = vorhanden ? uprightExif(vorhanden) : baueExif(aufnahme)
  if (!block) return gezeichnet

  try {
    const bytes = new Uint8Array(await gezeichnet.arrayBuffer())
    return new Blob([withExif(bytes, block)], { type: 'image/jpeg' })
  } catch {
    // Lieber ein Foto ohne Angaben als gar keines.
    return gezeichnet
  }
}

/**
 * Bildpunkte aus der Datei holen.
 *
 * `imageOrientation: 'from-image'` richtet das Bild nach der EXIF-Marke aus --
 * sonst läge ein Hochkant-Foto vom Telefon quer. Deshalb wird die Marke beim
 * Übernehmen auf „aufrecht" gesetzt: gedreht wurde schon.
 */
async function toBitmap(file: File): Promise<ImageBitmap> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      // Weiter mit dem Umweg unten.
    }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('Dieses Bild lässt sich nicht öffnen.'))
      image.src = url
    })
    return await createImageBitmap(image)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Ob die Datei überhaupt ein Bild ist -- Videos kommen später. */
export function isImage(file: File): boolean {
  return (
    file.type.startsWith('image/') ||
    // Ohne `type` -- das passiert über die Dateien-App -- entscheidet der Name.
    // `dng` gehört dazu, seit eine ProRAW-Aufnahme im Betrieb aufschlug.
    /\.(hei[cf]|jpe?g|png|webp|gif|dng|tiff?)$/i.test(file.name)
  )
}
